import { useMemo, type PropsWithChildren } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClientProvider } from "@/api/zustandQuery";
import { QueryClientProvider as ServerQueryClientProvider } from "@tanstack/react-query";
import { AppErrorBoundary } from "@/components/feedback/AppErrorBoundary";
import { DialogProvider } from "@/components/ui/DialogProvider";
import { createAppTheme } from "@/theme/theme";
import { ThemeModeProvider, useThemeMode } from "@/theme/ThemeModeContext";
import { AuthProvider } from "@/api/auth";
import { queryClient } from "@/api/queryClient";
import { serverQueryClient } from "@/api/serverQueryClient";
import { SocketProvider } from "@/hooks/useSocket";

function ThemedProviders({ children }: PropsWithChildren) {
  const { themeMode } = useThemeMode();
  const appTheme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <AppErrorBoundary>{children}</AppErrorBoundary>
    </ThemeProvider>
  );
}

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <ServerQueryClientProvider client={serverQueryClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SocketProvider>
            <ThemeModeProvider>
              <ThemedProviders>
                <DialogProvider>{children}</DialogProvider>
              </ThemedProviders>
            </ThemeModeProvider>
          </SocketProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ServerQueryClientProvider>
  );
}
